'use client';

import type { FilterOperator } from '@/components/table-filters/constants';
import { getList } from '@/requests/api/get-list';
import {
  MAX_PAGE_SIZE,
  NESTED_KEY_SEPARATOR,
  QUERY_KEYS,
} from '@/requests/constants';
import {
  chunkIdsByByteSize,
  filterNested,
  mergeDataWithNested,
} from '@/requests/hooks/utils';
import type { ListResponse, QueryParams } from '@/requests/types';
import type { BaseInstance, ModelName } from '@pigello/pigello-matrix';
import {
  useQueries,
  type UseQueryOptions,
  type UseQueryResult,
} from '@tanstack/react-query';
import { useCallback, useMemo } from 'react';
import type { SnakeCase } from 'type-fest';
import { getCount } from '../api/get-count';
import { useFetchNestedQueries } from './use-fetch-nested';

export function useFetchAllIds<TInstance extends BaseInstance>(
  options: Omit<
    UseQueryOptions<unknown, unknown, ListResponse<TInstance>, Array<string>>,
    'queryKey' | 'queryFn'
  > & {
    modelName: ModelName;
    order?: QueryParams<TInstance>['order'];
    filters?: QueryParams<TInstance>['filters'];
    nested?: (keyof TInstance)[] | undefined;
    slim?: boolean;
    ids: string[];
    idAttr: SnakeCase<keyof TInstance> | (string & {});
    search?: string;
    fetchAllManyRelations?: boolean;
    operator?: FilterOperator;
    overrideUrl?: string;
    overrideUrlReplaceAll?: boolean;
  }
) {
  const {
    modelName,
    order,
    filters,
    nested = [],
    slim,
    idAttr,
    ids,
    search,
    fetchAllManyRelations = false,
    operator = '__in',
    overrideUrl,
    overrideUrlReplaceAll,
  } = options;

  /**
   * Filter out fields that should never be fetched to avoid unnecessary data fetching.
   */
  const nestedKey = nested.join(NESTED_KEY_SEPARATOR);
  const filteredNested = useMemo(
    () => filterNested<TInstance>(nestedKey),
    [nestedKey]
  );
  const chunkedIds = chunkIdsByByteSize(Array.from(new Set(ids)));

  const counts = useQueries({
    queries: chunkedIds.map((chunk) => ({
      queryKey: [
        modelName,
        idAttr,
        filters,
        chunk,
        operator,
        overrideUrl,
        overrideUrlReplaceAll,
      ],
      queryFn: async () =>
        await getCount({
          modelName,
          overrideUrl,
          overrideUrlReplaceAll,
          filters: {
            [idAttr]: { [operator]: chunk },
            ...filters,
          },
        }),
      enabled: options.enabled && (chunk?.length ?? 0) > 0,
    })),
  });

  const isCountSuccess = counts.every((query) => query.isSuccess);
  const isCountsLoading = counts.some((query) => query.isLoading);

  const batchedQueries = counts
    .map((chunk, index) => {
      const count = chunk.data;
      const pageAmount = count ? (count < 500 ? 1 : Math.ceil(count / 500)) : 0;
      const idChunk = chunkedIds.at(index);
      return Array.from({ length: pageAmount }).map((_, page) => {
        const remainingItems = (count ?? 0) - (page - 1) * MAX_PAGE_SIZE;
        const pageSize =
          remainingItems <= 0 ? 0 : Math.min(remainingItems, MAX_PAGE_SIZE);
        const isCountLessThanOrEqualToMaxPageSize =
          (count ?? 0) <= MAX_PAGE_SIZE;
        return {
          queryKey: [
            modelName,
            QUERY_KEYS.ALL_IDS,
            filters,
            slim,
            idAttr,
            idChunk,
            order,
            search,
            operator,
            page + 1,
            overrideUrl,
            overrideUrlReplaceAll,
            // isCountLessThanOrEqualToMaxPageSize ? count : pageSize,
          ],
          queryFn: async () => {
            const res = await getList<TInstance>({
              modelName,
              overrideUrl,
              overrideUrlReplaceAll,
              queryParams: {
                page: page + 1,
                pageSize: isCountLessThanOrEqualToMaxPageSize
                  ? count
                  : pageSize,
                search,
                filters: {
                  [idAttr]: { [operator]: idChunk },
                  ...filters,
                },
                slim,
                order,
              },
            });
            return res.list;
          },
          enabled:
            options.enabled &&
            !!idChunk?.length &&
            idChunk.length > 0 &&
            isCountSuccess &&
            pageSize > 0,
        };
      });
    })
    .flatMap((query) => query);

  const combine = useCallback(
    (results: UseQueryResult<TInstance[], Error>[]) => {
      const instances =
        results
          .flatMap((result) => result.data)
          .filter((res) => res !== undefined) ?? [];
      return {
        data: {
          list: instances,
          meta: {
            total_amount: instances.length,
            page: 1,
            page_amount: 1,
            page_size: instances.length,
          },
        },
        isLoading: results.some((result) => result.isLoading),
        isError: results.some((result) => result.isError),
        isFetching: results.some((result) => result.isFetching),
        isPending: results.some((result) => result.isPending),
        isRefetching: results.some((result) => result.isRefetching),
        isStale: results.every((result) => result.isStale),
        isSuccess: results.every((result) => result.isSuccess),
      };
    },
    []
  );

  const mainQueries = useQueries({
    queries: batchedQueries,
    combine: combine,
  });

  /**
   * Use React Query's useQueries to fetch all nested relations in parallel.
   */
  const nestedQueries = useFetchNestedQueries(
    modelName,
    mainQueries.data.list ?? [],
    filteredNested,
    fetchAllManyRelations
  );

  /**
   * Merge the aggregated nested data with the main list data.
   */
  const mergedData = useMemo(() => {
    if (
      !mainQueries.data ||
      !nestedQueries.data ||
      Object.keys(nestedQueries.data).length < 1
    ) {
      return mainQueries.data;
    }
    return mergeDataWithNested(
      mainQueries.data,
      nestedQueries.data,
      filteredNested
    );
  }, [mainQueries.data, nestedQueries.data, filteredNested]);

  return {
    data: mergedData,
    isLoading:
      nestedQueries.isLoading || mainQueries.isLoading || isCountsLoading,
    isFetching: nestedQueries.isFetching || mainQueries.isFetching,
    isPending: nestedQueries.isPending || mainQueries.isPending,
    isAllSuccess: mainQueries.isSuccess && nestedQueries.isSuccess,
    isError: nestedQueries.isError || mainQueries.isError,
    isRefetching: nestedQueries.isRefetching || mainQueries.isRefetching,
    isStale: nestedQueries.isStale || mainQueries.isStale,
  };
}
